import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import useAdminData from '../components/shared/useAdminData';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import Modal from '../components/common/Modal';
import StatusBadge from '../components/common/StatusBadge';
import DataTable from '../components/common/DataTable';
import { API_ENDPOINTS } from '../config/api-endpoints';
import './AbuseDetection.css';

const { Skeleton } = SkeletonLoaderComponents;

const AbuseDetection = () => {
  const { user } = useAdminAuth();
  const [thresholds, setThresholds] = useState({
    requestsPerHour: 1000,
    requestsPerDay: 10000,
    errorRate: 0.1, // 10%
    bandwidthMB: 1000
  });
  const [editingThresholds, setEditingThresholds] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionModal, setActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch flagged users and usage metrics
  const { data: usageData, loading, error: fetchError, refetch } = useAdminData(
    'abuse-detection',
    API_ENDPOINTS.ADMIN.ABUSE_DETECTION,
    {}
  );

  const handleThresholdChange = (e) => {
    const { name, value } = e.target;
    setThresholds(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleSaveThresholds = async () => {
    setError(null);
    setSuccess(null);
    setProcessing(true);

    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();
      const response = await fetch(`${API_ENDPOINTS.ADMIN.ABUSE_DETECTION}/thresholds`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(thresholds)
      });

      if (!response.ok) {
        throw new Error('Failed to update thresholds');
      }

      setSuccess('Thresholds updated successfully');
      setEditingThresholds(false);
      refetch();
    } catch (err) {
      setError(err.message || 'Failed to update thresholds');
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenActionModal = (userRecord, action) => {
    setSelectedUser(userRecord);
    setActionType(action);
    setActionReason('');
    setActionModal(true);
  };

  const handleSubmitAction = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!actionReason.trim()) {
      setError('Please provide a reason for this action');
      return;
    }

    setProcessing(true);

    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();
      const response = await fetch(`${API_ENDPOINTS.ADMIN.ABUSE_DETECTION}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: selectedUser.userId,
          action: actionType,
          reason: actionReason,
          adminId: user.userId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to execute action');
      }

      setSuccess(`Action "${actionType}" executed successfully for user ${selectedUser.userId}`);
      setActionModal(false);
      setSelectedUser(null);
      refetch();
    } catch (err) {
      setError(err.message || 'Failed to execute action');
    } finally {
      setProcessing(false);
    }
  };

  const getViolationBadge = (violations) => {
    if (violations.length === 0) return <StatusBadge status="active" label="Clean" />;
    if (violations.length >= 3) return <StatusBadge status="error" label="Critical" />;
    if (violations.length >= 2) return <StatusBadge status="warning" label="High Risk" />;
    return <StatusBadge status="pending" label="Flagged" />;
  };

  const columns = [
    {
      key: 'userId',
      label: 'User ID',
      sortable: true
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true
    },
    {
      key: 'requestsPerHour',
      label: 'Requests/Hour',
      sortable: true,
      render: (row) => (
        <span className={row.requestsPerHour > thresholds.requestsPerHour ? 'abuse-detection__violation' : ''}>
          {row.requestsPerHour?.toLocaleString()}
        </span>
      )
    },
    {
      key: 'requestsPerDay',
      label: 'Requests/Day',
      sortable: true,
      render: (row) => (
        <span className={row.requestsPerDay > thresholds.requestsPerDay ? 'abuse-detection__violation' : ''}>
          {row.requestsPerDay?.toLocaleString()}
        </span>
      )
    },
    {
      key: 'errorRate',
      label: 'Error Rate',
      sortable: true,
      render: (row) => (
        <span className={row.errorRate > thresholds.errorRate ? 'abuse-detection__violation' : ''}>
          {((row.errorRate || 0) * 100).toFixed(2)}%
        </span>
      )
    },
    {
      key: 'bandwidthMB',
      label: 'Bandwidth (MB)',
      sortable: true,
      render: (row) => (
        <span className={row.bandwidthMB > thresholds.bandwidthMB ? 'abuse-detection__violation' : ''}>
          {(row.bandwidthMB || 0).toFixed(2)}
        </span>
      )
    },
    {
      key: 'violations',
      label: 'Status',
      render: (row) => getViolationBadge(row.violations || [])
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="abuse-detection__actions">
          <button
            onClick={() => handleOpenActionModal(row, 'warning')}
            className="abuse-detection__action-btn abuse-detection__action-btn--warning"
            title="Send Warning"
          >
            Warn
          </button>
          <button
            onClick={() => handleOpenActionModal(row, 'temporary_ban')}
            className="abuse-detection__action-btn abuse-detection__action-btn--temp-ban"
            title="Temporary Ban"
          >
            Temp Ban
          </button>
          <button
            onClick={() => handleOpenActionModal(row, 'permanent_ban')}
            className="abuse-detection__action-btn abuse-detection__action-btn--perm-ban"
            title="Permanent Ban"
          >
            Perm Ban
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="abuse-detection">
        <div className="abuse-detection__header">
          <h1 className="abuse-detection__title">Abuse Detection</h1>
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} width="100%" height="80px" className="admin-skeleton-gap" />
        ))}
      </div>
    );
  }

  const flaggedUsers = usageData?.flaggedUsers || [];
  const currentThresholds = usageData?.thresholds || thresholds;

  return (
    <ErrorBoundary>
      <div className="abuse-detection">
        <div className="abuse-detection__header">
          <h1 className="abuse-detection__title">Abuse Detection</h1>
        </div>

        {error && (
          <div className="abuse-detection__alert abuse-detection__alert--error">
            {error}
          </div>
        )}

        {success && (
          <div className="abuse-detection__alert abuse-detection__alert--success">
            {success}
          </div>
        )}

        {fetchError && (
          <div className="abuse-detection__alert abuse-detection__alert--error">
            Failed to load usage data: {fetchError}
          </div>
        )}

        {/* Thresholds Configuration */}
        <div className="abuse-detection__thresholds">
          <div className="abuse-detection__thresholds-header">
            <h2 className="abuse-detection__section-title">Detection Thresholds</h2>
            {!editingThresholds ? (
              <button
                onClick={() => setEditingThresholds(true)}
                className="abuse-detection__btn abuse-detection__btn--secondary"
              >
                Edit Thresholds
              </button>
            ) : (
              <div className="abuse-detection__threshold-actions">
                <button
                  onClick={() => setEditingThresholds(false)}
                  className="abuse-detection__btn abuse-detection__btn--secondary"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveThresholds}
                  className="abuse-detection__btn abuse-detection__btn--primary"
                  disabled={processing}
                >
                  {processing ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>

          <div className="abuse-detection__threshold-grid">
            <div className="abuse-detection__threshold-item">
              <label className="abuse-detection__threshold-label">
                Requests per Hour
              </label>
              {editingThresholds ? (
                <input
                  type="number"
                  name="requestsPerHour"
                  value={thresholds.requestsPerHour}
                  onChange={handleThresholdChange}
                  className="abuse-detection__threshold-input"
                  min="0"
                />
              ) : (
                <span className="abuse-detection__threshold-value">
                  {currentThresholds.requestsPerHour?.toLocaleString()}
                </span>
              )}
            </div>

            <div className="abuse-detection__threshold-item">
              <label className="abuse-detection__threshold-label">
                Requests per Day
              </label>
              {editingThresholds ? (
                <input
                  type="number"
                  name="requestsPerDay"
                  value={thresholds.requestsPerDay}
                  onChange={handleThresholdChange}
                  className="abuse-detection__threshold-input"
                  min="0"
                />
              ) : (
                <span className="abuse-detection__threshold-value">
                  {currentThresholds.requestsPerDay?.toLocaleString()}
                </span>
              )}
            </div>

            <div className="abuse-detection__threshold-item">
              <label className="abuse-detection__threshold-label">
                Error Rate Threshold
              </label>
              {editingThresholds ? (
                <input
                  type="number"
                  name="errorRate"
                  value={thresholds.errorRate}
                  onChange={handleThresholdChange}
                  className="abuse-detection__threshold-input"
                  min="0"
                  max="1"
                  step="0.01"
                />
              ) : (
                <span className="abuse-detection__threshold-value">
                  {((currentThresholds.errorRate || 0) * 100).toFixed(0)}%
                </span>
              )}
            </div>

            <div className="abuse-detection__threshold-item">
              <label className="abuse-detection__threshold-label">
                Bandwidth (MB)
              </label>
              {editingThresholds ? (
                <input
                  type="number"
                  name="bandwidthMB"
                  value={thresholds.bandwidthMB}
                  onChange={handleThresholdChange}
                  className="abuse-detection__threshold-input"
                  min="0"
                />
              ) : (
                <span className="abuse-detection__threshold-value">
                  {currentThresholds.bandwidthMB?.toLocaleString()} MB
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Flagged Users Table */}
        <div className="abuse-detection__flagged">
          <h2 className="abuse-detection__section-title">
            Flagged Users ({flaggedUsers.length})
          </h2>

          {flaggedUsers.length === 0 ? (
            <div className="abuse-detection__empty">
              No users currently flagged for abuse. All usage is within normal thresholds.
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={flaggedUsers}
              defaultSortKey="violations"
              defaultSortDirection="desc"
            />
          )}
        </div>

        {/* Action Modal */}
        {actionModal && selectedUser && (
          <Modal
            title={`${actionType.replace('_', ' ').toUpperCase()} - ${selectedUser.email}`}
            onClose={() => setActionModal(false)}
          >
            <form onSubmit={handleSubmitAction} className="abuse-detection__action-form">
              <div className="abuse-detection__user-info">
                <p><strong>User ID:</strong> {selectedUser.userId}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
                <p><strong>Violations:</strong> {selectedUser.violations.join(', ')}</p>
              </div>

              <div className="abuse-detection__form-group">
                <label htmlFor="actionReason" className="abuse-detection__label">
                  Reason for Action *
                </label>
                <textarea
                  id="actionReason"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  className="abuse-detection__textarea"
                  placeholder="Provide a detailed reason for this action..."
                  rows="4"
                  required
                />
              </div>

              <div className="abuse-detection__form-actions">
                <button
                  type="button"
                  onClick={() => setActionModal(false)}
                  className="abuse-detection__btn abuse-detection__btn--secondary"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="abuse-detection__btn abuse-detection__btn--danger"
                  disabled={processing}
                >
                  {processing ? 'Processing...' : `Confirm ${actionType.replace('_', ' ')}`}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default AbuseDetection;
